import React, { useState } from 'react'
import { Formik } from 'formik'

import Box from '../kit/box'
import FlatList from '../kit/flat-list'
import Form from '../kit/form'
import Loading from '../kit/loading'
import Pressable from '../kit/pressable'
import Text from '../kit/text'
import CheckBoxWrapper from '../kit/form/inputs/checkbox-input/checkbox-wrapper'
import { ChevronDownIcon, CloseIcon } from '../kit/icons'
import { colors } from '@theme'

import { ISearchedMed } from '@store/Meds/types'
import { useMedsStore } from '@store/Meds'
import { TextFieldFormated } from '../formFields'

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export type SelectedMedGpi = {
  gpi: string
  drug_name: string
  displayLabel: string
}

export type MedicationSearchSelectProps = {
  selectedItems: SelectedMedGpi[]
  onSelectionChange: (items: SelectedMedGpi[]) => void
  label?: string
  minHeight?: string
  maxHeight?: string
  showsScrollIndicator?: boolean
}

/* -----------------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------------- */

export const getSelectedGpis = (items: SelectedMedGpi[]) =>
  items.map(i => i.gpi)

export const getMedicationSummary = (items: SelectedMedGpi[]) =>
  [...new Set(items.map(i => i.drug_name))].join(', ')

/* -----------------------------------------------------------------------------
 * SelectedMedChips
 * -------------------------------------------------------------------------- */

function SelectedMedChips({
  selectedItems,
  onRemove
}: {
  selectedItems: SelectedMedGpi[]
  onRemove: (gpi: string) => void
}) {
  if (selectedItems.length === 0) {
    return null
  }

  return (
    <Box flexDirection='row' flexWrap='wrap' gap='08' marginBottom='12'>
      {selectedItems.map(item => (
        <Box
          key={item.gpi}
          flexDirection='row'
          alignItems='center'
          paddingX='08'
          paddingY='04'
          // @ts-ignore
          style={{
            borderRadius: '12px',
            borderWidth: '1px',
            borderColor: colors.walrusBlue500,
            backgroundColor: '#F0F4FF'
          }}
        >
          <Text
            font='display.12.bold'
            textColor='walrusBlue500'
            marginRight='04'
          >
            {item.drug_name} ({item.displayLabel})
          </Text>
          <Pressable onPress={() => onRemove(item.gpi)}>
            <CloseIcon fill='walrusBlue500' width={14} height={14} />
          </Pressable>
        </Box>
      ))}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MedicationSearchItem
 * -------------------------------------------------------------------------- */

interface MedicationSearchItemProps {
  item: ISearchedMed
  selectedGpis: Set<string>
  onToggleGpi: (
    medication: ISearchedMed,
    gpi: string,
    displayLabel: string
  ) => void
}

function MedicationSearchItem({
  item,
  selectedGpis,
  onToggleGpi
}: MedicationSearchItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasAnySelected = item.dosage_forms.some(form =>
    form.strengths?.some(
      strength => strength.gpi && selectedGpis.has(strength.gpi)
    )
  )

  return (
    <Pressable>
      <Box
        padding='16'
        marginY='04'
        marginRight='08'
        marginLeft='04'
        // @ts-ignore
        style={{
          borderRadius: '12px',
          borderWidth: '1px',
          borderColor: hasAnySelected ? colors.walrusBlue500 : '#F9F9F9',
          boxShadow: `0px 2px 4px 0px ${
            hasAnySelected ? colors.walrusBlue500 : 'rgba(62, 64, 136, 0.08)'
          }`
        }}
      >
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Text
            font='display.16.bold'
            marginLeft='16'
            textColor={hasAnySelected ? 'walrusBlue500' : 'walrusBlue200'}
          >
            {item.drug_name}
          </Text>

          <Pressable
            onPress={() => setIsExpanded(!isExpanded)}
            justifyContent='center'
            alignItems='center'
          >
            <Box
              style={{
                transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }]
              }}
              width='20'
              height='20'
              justifyContent='center'
              alignItems='center'
            >
              <ChevronDownIcon fill={'shade07'} width={20} height={20} />
            </Box>
          </Pressable>
        </Box>
        {isExpanded &&
          item.dosage_forms.map(form =>
            (form.strengths || []).map(strength => {
              if (!strength.gpi) {
                return null
              }
              const isSelected = selectedGpis.has(strength.gpi)
              const displayLabel = `${form.form} - ${strength.value}`
              return (
                <Pressable
                  key={strength.gpi}
                  onPress={() =>
                    onToggleGpi(item, strength.gpi as string, displayLabel)
                  }
                >
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    marginTop='08'
                    gap='08'
                  >
                    <CheckBoxWrapper
                      isChecked={isSelected}
                      size={16}
                      children={null}
                    />
                    <Text
                      font='display.14.bold'
                      textColor={isSelected ? 'walrusBlue500' : 'walrusBlue200'}
                    >
                      {displayLabel}
                    </Text>
                  </Box>
                </Pressable>
              )
            })
          )}
      </Box>
    </Pressable>
  )
}

/* -----------------------------------------------------------------------------
 * MedicationSearchSelect
 * -------------------------------------------------------------------------- */

export function MedicationSearchSelect({
  selectedItems,
  onSelectionChange,
  label = 'Quick Search',
  minHeight = '160px',
  maxHeight = '240px',
  showsScrollIndicator = false
}: MedicationSearchSelectProps) {
  const { searchMedsByName } = useMedsStore.getState()
  const [isLoading, setIsLoading] = useState(false)
  const [lastTimeout, setLastTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null)
  const [medName, setMedName] = useState('')
  const [medications, setMedications] = useState<ISearchedMed[]>([])
  const [preventTyping, setPreventTyping] = useState(false)

  const selectedGpis = new Set(selectedItems.map(item => item.gpi))

  const onEmpty = () => {
    setIsLoading(false)
    setMedName('')
    if (lastTimeout) {
      clearTimeout(lastTimeout)
    }
    setLastTimeout(null)
    setMedications([])
  }

  const searchMeds = async (name: string) => {
    setIsLoading(true)
    try {
      const meds = await searchMedsByName(name)
      const selectedDrugNames = new Set(
        selectedItems.map(item => item.drug_name)
      )
      const merged = [...(meds || [])]
      // Pin selected meds to top when present in results
      selectedDrugNames.forEach(drugName => {
        const index = merged.findIndex(med => med.drug_name === drugName)
        if (index > 0) {
          const [med] = merged.splice(index, 1)
          merged.unshift(med)
        }
      })
      setMedications(merged)
    } finally {
      setIsLoading(false)
      setPreventTyping(false)
    }
  }

  const onTyping = (content: string) => {
    if (preventTyping) {
      return
    }
    if (content === '') {
      onEmpty()
      return
    }
    setIsLoading(true)
    setMedName(content)
    if (lastTimeout) {
      clearTimeout(lastTimeout)
    }
    const typingTimeOut = setTimeout(() => {
      searchMeds(content)
    }, 2000)
    setLastTimeout(typingTimeOut)
  }

  const handleToggleGpi = (
    medication: ISearchedMed,
    gpi: string,
    displayLabel: string
  ) => {
    if (selectedGpis.has(gpi)) {
      onSelectionChange(selectedItems.filter(item => item.gpi !== gpi))
      return
    }
    onSelectionChange([
      ...selectedItems,
      {
        gpi,
        drug_name: medication.drug_name,
        displayLabel
      }
    ])
  }

  const handleRemoveChip = (gpi: string) => {
    onSelectionChange(selectedItems.filter(item => item.gpi !== gpi))
  }

  return (
    <Box flexDirection='column' style={{ flexShrink: 1 }}>
      <Formik
        initialValues={{ medication: '' }}
        onSubmit={() => {}}
        validateOnChange={false}
      >
        {() => (
          <Form>
            <TextFieldFormated
              name='medication'
              label={label}
              value={medName}
              onChangeText={text => onTyping(text)}
            />
          </Form>
        )}
      </Formik>

      <SelectedMedChips
        selectedItems={selectedItems}
        onRemove={handleRemoveChip}
      />

      <Box overflowY='auto' style={{ minHeight, maxHeight }} marginBottom='16'>
        {medName === '' && medications.length === 0 && !isLoading && (
          <Box marginTop='24'>
            <Text font='text.12.medium' textColor='walrusBlue200'>
              Start typing to search for medications
            </Text>
          </Box>
        )}
        {isLoading && (
          <Box paddingTop='16'>
            <Loading size='small' />
          </Box>
        )}
        {!!medName &&
          medications.length === 0 &&
          !isLoading &&
          !lastTimeout && (
            <Box marginTop='24'>
              <Text font='text.12.medium' textColor='walrusBlue200'>
                No medications found
              </Text>
            </Box>
          )}
        {medications.length > 0 && !isLoading && (
          <FlatList
            showsVerticalScrollIndicator={showsScrollIndicator}
            onEndReachedThreshold={0.5}
            data={medications}
            maxToRenderPerBatch={10}
            onTouchStart={() => setPreventTyping(true)}
            // @ts-ignore
            renderItem={({ item }: { item: ISearchedMed }) => (
              <MedicationSearchItem
                item={item}
                selectedGpis={selectedGpis}
                onToggleGpi={handleToggleGpi}
              />
            )}
            keyExtractor={(item: ISearchedMed) => item.drug_name}
          />
        )}
      </Box>
    </Box>
  )
}

export default MedicationSearchSelect
